import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import 'bulma/css/bulma.min.css';

import { SampleConfigurationSet } from '../../sampleSets.ts';
import { BeamlineContext } from '../../beamlineProvider.tsx';
import { SampleSetsContext } from '../../sampleSetsProvider.tsx';
import { SelectingStatus } from '../../components/inputAutocomplete.tsx';
import { SetAutocomplete, SetSearchFunctions } from '../../components/setAutocomplete.tsx';
import { sortWithNumberParsing } from "../../utils.tsx";


interface MoveSamplesProps {
  selection: string[];
}

const MoveSamples: React.FC<MoveSamplesProps> = (props) => {

  var { setSlug } = useParams();

  const beamlineContext = useContext(BeamlineContext);
  const sampleSetsContext = useContext(SampleSetsContext);
  const thisSet = setSlug ? sampleSetsContext.sets.getBySlug(setSlug) : undefined;

  const [setValue, setSetValue] = useState<SampleConfigurationSet | null>(thisSet || null);
  const [conflictMessage, setConflictMessage] = useState<React.JSX.Element | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);


  function clickedOpen() {
    if (!inProgress && !isOpen && thisSet) {
      setIsOpen(true);
      checkForNameConflicts(setValue);
    }
  }


  function checkForNameConflicts(item: SampleConfigurationSet | null) {
    if (!thisSet || !item || thisSet.slug === item.slug) {
      setConflictMessage(null);
      return;
    }
    const currentDestinationSampleNames = new Set<string>(item.all().map(s => s.name));
    const samplesToMove = props.selection.map((slug) => thisSet.samplesBySlug.get(slug)!);
    const samplesWithConflictingNames = samplesToMove.filter((s) => currentDestinationSampleNames.has(s.name));
    // If we need to add indexes, we should add them in an orderly fashion
    if (samplesWithConflictingNames.length > 0) {
      const conflictingNames = samplesWithConflictingNames.map(s => s.name).sort((a, b) => sortWithNumberParsing(a, b));
      if (conflictingNames.length > 10) {
        setConflictMessage(
          <div>{ conflictingNames.length } samples have names that conflict with names in the destination set. They will be renamed by adding an index to the end of their name.</div>
        );
      } else {
      setConflictMessage(
        <div>The following samples will be renamed by adding an index:
          <ul style={{ listStyleType: "disc", marginLeft: "2em" }}>
            {conflictingNames.map((name, index) => <li key={index}>{name}</li>)}
          </ul>
        </div>);
      }
    } else {
      setConflictMessage(null);
    }
  }


  const setSearchFunctions:SetSearchFunctions = {
    itemSelected: (item: SampleConfigurationSet) => {
      setSetValue(item);
      checkForNameConflicts(item);
      return SelectingStatus.Success
    },
    selectedNone: () => {
      checkForNameConflicts(null);
      setSetValue(null);
      return SelectingStatus.Success
    }
  };


  async function pressedSubmit() {
    if (!thisSet) { return; }
    setSubmitErrorMessage(null);
    setInProgress(true);
    const samplesToMove = props.selection.map((slug) => thisSet.samplesBySlug.get(slug)!);
    const result = await sampleSetsContext.sets.moveSamplesToSet(samplesToMove, setValue!.slug);
    sampleSetsContext.changed();
    setInProgress(false);
    if (!result.success) {
      setSubmitErrorMessage(result.message || "");
    } else {
      setIsOpen(false);
    }
  };

  function clickedClose() {
    if (!inProgress) { setSubmitErrorMessage(null); setIsOpen(false); }
  }

  const plural = props.selection.length > 1 ? "s" : "";

  return (
    <a className="dropdown-item" onClick={ clickedOpen }>Move Sample{ plural }

      <div id="modal-add-sample" className={ isOpen ? "modal is-active" : "modal" }
        style={{ whiteSpace: "normal" }}>
        <div className="modal-background"></div>

        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Move Sample{ plural }</p>
            <button className="delete" aria-label="close" onClick={ clickedClose }></button>

          </header>
          <section className="modal-card-body">
    
            <div className="field">
              <label className="label">
                Select a destination { beamlineContext.setNoun } to move { props.selection.length } sample{ plural } to:
              </label>
                <SetAutocomplete
                    selectedItem={ setValue }
                    searchFunctions={ setSearchFunctions }
                    canSelectNone={ false }
                    showClearButton={ false }
                />
            </div>

            { conflictMessage && (
              <article className="message is-warning">
                <div className="message-body">{ conflictMessage }</div>
              </article>
            )}

            <div className="buttons">
              <input
                type="button"
                className="button is-primary"
                onClick={ pressedSubmit }
                disabled={ inProgress || !setValue || setValue.slug === thisSet?.slug }
                value="Move"
              />
              <button className="button" onClick={ clickedClose }>Cancel</button>
            </div>

            { submitErrorMessage && (
              <article className="message is-danger">
                <div className="message-body">
                  { submitErrorMessage }
                </div>
              </article>
            )}

          </section>

        </div>
      </div>

    </a>
  )
}
export default MoveSamples
